import { type GetCountryCodesQuery } from "#graphql/supergraphTypes";
import { type DetailedPaymentPrefUnionObjectType } from "../../common/types";
import { type SelectedBankCountry } from "../../Context/types";

/**
 * Maps a raw CountryObjectType from the payment pref detail to a SelectedBankCountry.
 * Only SEPA, WIRE, and CHECK carry a persisted bank/address country on their detail fragment.
 * ACH/BECS/BACS/EFT use fixed hardcoded alpha2codes and do not need pref-based extraction.
 */
export const getCountryFromPaymentPrefDetail = (
  detail: DetailedPaymentPrefUnionObjectType | undefined,
): SelectedBankCountry | undefined => {
  if (!detail) return undefined;

  if (detail.__typename === "SepaPaymentPrefObjectType") {
    const c = detail.bank?.address?.country;
    return c ? { id: c.id, name: c.name, alpha2code: c.alpha2code ?? null } : undefined;
  }

  if (detail.__typename === "WirePaymentPrefObjectType") {
    const c = detail.bank?.address?.country;
    return c ? { id: c.id, name: c.name, alpha2code: c.alpha2code ?? null } : undefined;
  }

  if (detail.__typename === "CheckPaymentPrefObjectType") {
    const c = detail.address?.country;
    return c ? { id: c.id, name: c.name, alpha2code: c.alpha2code ?? null } : undefined;
  }

  return undefined;
};

/**
 * Normalizes a country node from the country query into a SelectedBankCountry object.
 */
export const toSelectedBankCountry = (
  node: NonNullable<
    NonNullable<NonNullable<GetCountryCodesQuery["countries"]>["edges"]>[number]
  >["node"],
): SelectedBankCountry => ({
  id: node.id,
  name: node.name,
  alpha2code: node.alpha2code ?? null,
});

/**
 * Finds a country in the country data by its Alpha2 code (e.g., "US", "AU").
 */
export const findCountryByAlpha2Code = (
  countryData: GetCountryCodesQuery | undefined,
  alpha2Code: string | undefined,
): SelectedBankCountry | undefined => {
  if (!countryData || !alpha2Code) return undefined;
  const match = countryData.countries?.edges?.find(
    (edge) => edge?.node?.alpha2code?.toLowerCase() === alpha2Code.toLowerCase(),
  );
  return match?.node ? toSelectedBankCountry(match.node) : undefined;
};

/**
 * Finds a country in the country data by its Relay ID.
 */
export const findCountryById = (
  countryData: GetCountryCodesQuery | undefined,
  id: string | undefined,
): SelectedBankCountry | undefined => {
  if (!countryData || !id) return undefined;
  const match = countryData.countries?.edges?.find((edge) => edge?.node?.id === id);
  return match?.node ? toSelectedBankCountry(match.node) : undefined;
};

/**
 * Finds a country in the country data by its name (needed for Check mailing address fallback).
 */
export const findCountryByName = (
  countryData: GetCountryCodesQuery | undefined,
  name: string | undefined,
): SelectedBankCountry | undefined => {
  if (!countryData || !name) return undefined;
  const match = countryData.countries?.edges?.find((edge) => edge?.node?.name === name);
  return match?.node ? toSelectedBankCountry(match.node) : undefined;
};
